import { h, Component } from 'preact'
import { GraphTitle } from '../GraphTitle'
import { Loader } from '../Loader'
import { getBrandBlue, getBrandBorderGrey, getBrandGrey } from '../utils'
import 'chartjs-plugin-crosshair'
import { Bar } from 'react-chartjs-2'
import { ChartData, ChartOptions, ChartTooltipItem } from 'chart.js'
import AnalyticsDateFormatter from '../AnalyticsDateFormatter'
import axios from 'axios'
import { TotalVoteData } from '../interfaces'

interface Props {
    shouldChartRedraw: boolean,
    isIdeaStatusMappingSetup: boolean,
    startDate: Date,
    endDate: Date
}

interface State {
    isLoading: boolean,
    totalVotes: Array<TotalVoteData>,
}

class PercentageTotalVotesPerMonth extends Component<Props, State> {
    private brandBlue = getBrandBlue()
    private brandBorderGrey = getBrandBorderGrey()
    private brandGrey = getBrandGrey()
    constructor (props: Props) {
        super(props)
        this.setState({
            isLoading: true,
            totalVotes: []
        })
    }

    async componentDidUpdate (prevProps: Props, prevState: State) {
        if (this.props.startDate !== prevProps.startDate || this.props.endDate !== prevProps.endDate || this.props.isIdeaStatusMappingSetup !== prevProps.isIdeaStatusMappingSetup) {
            await this.fetchData()
        }
    }

    async fetchData () {
        if (!this.props.isIdeaStatusMappingSetup) {
            return
        }
        this.setState({ isLoading: true })
        const { startDate, endDate } = this.props
        const res = await axios.post('/analyticsV2/totalVotesOverTime', {
            filters: {
                start: AnalyticsDateFormatter.formatDate(startDate),
                end: AnalyticsDateFormatter.formatDate(endDate, false),
                group: 'monthly'
            }
        })
        this.setState({
            totalVotes: res.data,
            isLoading: false
        })
    }

    getBarChartDataDataSet (data: Array<TotalVoteData>) {
        return {
            labels: data.map(d => d.date),
            datasets: [
                {
                    label: '% Total idea votes delivered',
                    fill: false,
                    data: data.map((value, index) => {
                        const totalVotes = value.open + value.delivered
                        let percentVotesDelivered = 0
                        if (totalVotes > 0) {
                            percentVotesDelivered = (value.delivered / (value.open + value.delivered)) * 100
                        }
                        return Number(percentVotesDelivered.toFixed(2))
                    }),
                    borderColor: this.brandBlue,
                    backgroundColor: this.brandBlue
                }
            ]
        }
    }

    getBarChartOptions (): ChartOptions {
        return {
            title: {
                display: false
            },
            layout: {
                padding: {
                    left: 0,
                    right: 0,
                    top: 0,
                    bottom: 24
                }
            },
            responsive: true,
            maintainAspectRatio: false,
            tooltips: {
                intersect: false,
                mode: 'x',
                callbacks: {
                    title (item: ChartTooltipItem[], data: ChartData): string | string[] {
                        if (item.length > 0 && item[0].label) {
                            const date = new Date(item[0].label.substr(0, 10))
                            return new Intl.DateTimeFormat('en', { month: 'short', year: 'numeric' }).format(date)
                        }
                        return ''
                    }
                }
            },
            legend: {
                display: false,
                position: 'top',
                align: 'start',
                labels: {
                    boxWidth: 12
                }
            },
            scales: {
                xAxes: [{
                    offset: true,
                    type: 'time',
                    time: {
                        minUnit: 'month'
                    },
                    gridLines: {
                        display: false
                    },
                    ticks: {
                        maxRotation: 0,
                        callback: (value, index, values) => {
                            return '      ' + value + '      '
                        }
                    }
                }],
                yAxes: [
                    {
                        gridLines: {
                            borderDash: [8, 4],
                            color: this.brandBorderGrey,
                            drawBorder: false
                        },
                        ticks: {
                            padding: 16,
                            beginAtZero: true
                        }
                    }
                ]
            },
            plugins: {
                crosshair: {
                    line: {
                        color: this.brandGrey, // crosshair line color
                        width: 0.5 // crosshair line width
                    },
                    zoom: {
                        enabled: false
                    },
                    sync: {
                        enabled: false
                    }
                }
            }
        }
    }

    render ({ shouldChartRedraw, isIdeaStatusMappingSetup }: Props, { isLoading, totalVotes }: State) {
        return <div className={`graph-row ${!isIdeaStatusMappingSetup ? 'graph-row-no-data' : ''}`}>
            <GraphTitle customClass='with-legend' title='% Total votes delivered per month'
                        tooltipMessage='The percentage of the cumulative total votes that have been delivered, shown on a monthly basis. Excludes closed votes.' />
            {!isIdeaStatusMappingSetup
                ? <div className='graph-row-no-data-text'>There's no data yet</div>
                : isLoading ? <Loader /> : <Bar redraw={shouldChartRedraw} data={this.getBarChartDataDataSet(totalVotes)} options={this.getBarChartOptions()} />}
        </div>
    }
}

export default PercentageTotalVotesPerMonth
